/**
 * Shared API response unwrapper utility.
 *
 * Many generated API service methods declare their return type as
 * `Observable<ApiResponse>` — a wrapper with `{ success, data, error? }`.
 * At runtime, however, the `apiResponseInterceptor` typically unwraps this
 * envelope before the service layer receives the value.  This means the
 * actual runtime value may be either:
 *   (a) the full `ApiResponse` envelope (in tests or when the interceptor
 *       is bypassed), or
 *   (b) the inner `data` payload directly.
 *
 * `extractApiPayload` handles both cases in a single controlled cast point.
 * All feature-level mapper files should delegate to this function rather than
 * implementing their own unwrap logic.
 *
 * --- Controlled cast point ---
 * The `as T` cast here is intentional and unavoidable: the generated
 * `ApiResponse.data` is typed as `object`, but at runtime it carries the
 * concrete payload shape.  By centralising the cast we keep the rest of the
 * codebase free of `as unknown as` gymnastics.
 */

/**
 * Extract the inner payload from an API response value.
 *
 * @param response - Either a full `ApiResponse` envelope (`{ success, data }`)
 *   or the already-unwrapped payload (when the interceptor has run).
 * @returns The concrete payload typed as `T`.
 *
 * Detection logic uses `typeof response.success === 'boolean'` rather than
 * a bare `'success' in response` check, which is more specific and avoids
 * false positives when the payload itself happens to have a `success` field
 * of a non-boolean type.
 */
export class ApiEnvelopeError extends Error {
  readonly code = "api_envelope_failure";

  constructor(message = "API response reported failure") {
    super(`[api_envelope_failure] ${message}`);
    this.name = "ApiEnvelopeError";
  }
}

export function extractApiPayload<T>(response: unknown): T {
  if (
    response != null &&
    typeof response === "object" &&
    "success" in response &&
    (response as Record<string, unknown>)["success"] === false
  ) {
    const error = (response as Record<string, unknown>)["error"];
    const detail =
      error != null && typeof error === "object"
        ? (error as Record<string, unknown>)["message"]
        : undefined;
    throw new ApiEnvelopeError(
      typeof error === "string" && error.trim().length > 0
        ? error.trim()
        : typeof detail === "string" && detail.trim().length > 0
          ? detail.trim()
          : undefined,
    );
  }
  if (
    response != null &&
    typeof response === "object" &&
    "success" in response &&
    typeof (response as Record<string, unknown>)["success"] === "boolean" &&
    "data" in response
  ) {
    const envelope = response as Record<string, unknown>;
    return envelope["data"] as T;
  }
  // Interceptor already unwrapped — response IS the inner payload.
  return response as T;
}

/**
 * Return true only when an order-create response explicitly reports that its
 * durable submission lifecycle is unresolved and requires reconciliation.
 *
 * The generated client can lag the Rust OpenAPI schema during an atomic source
 * + regeneration change. Keeping this narrow structural check app-owned lets
 * order-entry surfaces behave truthfully before and after regeneration without
 * widening the generated model or mislabelling a reconciliation-required
 * response as completed.
 */
export function isOrderReconciliationRequired(response: unknown): boolean {
  if (response === null || typeof response !== "object") {
    return false;
  }

  const record = response as Record<string, unknown>;
  const submissionState =
    record["submissionState"] ?? record["submission_state"];
  const createDisposition =
    record["createDisposition"] ?? record["create_disposition"];
  return (
    createDisposition === "pending_reconciliation" ||
    submissionState === "dispatching" ||
    submissionState === "reconciliation_required"
  );
}

/**
 * The mutually exclusive, user-visible outcomes of a successful order-create
 * HTTP response. A 2xx response is only transport success: the durable order
 * can still be reconciling, working, partially filled, or terminal.
 */
export type CreateOrderOutcomeKind =
  | "reconciliation"
  | "working"
  | "partially_filled"
  | "filled"
  | "terminal"
  | "unsupported";

export interface CreateOrderOutcome {
  readonly kind: CreateOrderOutcomeKind;
  readonly status: string | null;
  readonly simulated: boolean;
  readonly mode: "live" | "paper";
  /** Exact filled quantity when the response contract supplies one. */
  readonly filledQuantity: string | null;
  /** True when a terminal result still changed exposure through a partial fill. */
  readonly hasExecution: boolean;
}

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ISO_TIMESTAMP =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const LIVE_ORDER_EXCHANGES = new Set([
  "binance",
  "coinbase",
  "kraken",
  "okx",
  "bybit",
  "bitget",
  "gateio",
  "kucoin",
  "internal",
]);

/**
 * Validate the durable identity and venue metadata required before a create
 * acknowledgement can be presented as success or release its retry key.
 */
export function hasValidCreateOrderIdentity(
  response: unknown,
  simulated: boolean,
): boolean {
  if (response === null || typeof response !== "object") return false;
  const record = response as Record<string, unknown>;
  const id = record["id"];
  const exchange = record["exchange"];
  const createdAt = record["createdAt"] ?? record["created_at"];
  const updatedAt = record["updatedAt"] ?? record["updated_at"];
  const venueClientOrderId =
    record["venueClientOrderId"] ?? record["venue_client_order_id"];

  if (
    typeof id !== "string" ||
    !UUID.test(id) ||
    typeof exchange !== "string" ||
    exchange.trim() !== exchange ||
    typeof createdAt !== "string" ||
    !ISO_TIMESTAMP.test(createdAt) ||
    !Number.isFinite(Date.parse(createdAt))
  ) {
    return false;
  }

  if (simulated) return exchange === "paper";
  return (
    LIVE_ORDER_EXCHANGES.has(exchange) &&
    typeof updatedAt === "string" &&
    ISO_TIMESTAMP.test(updatedAt) &&
    Number.isFinite(Date.parse(updatedAt)) &&
    typeof venueClientOrderId === "string" &&
    venueClientOrderId.trim().length > 0 &&
    venueClientOrderId.trim() === venueClientOrderId
  );
}

/**
 * Classify an order-create result without treating HTTP success as execution
 * success. Both generated PascalCase values and canonical snake_case values
 * are accepted while unknown lifecycle values fail closed as `unsupported`.
 */
export function classifyCreateOrderOutcome(
  response: unknown,
): CreateOrderOutcome {
  if (response === null || typeof response !== "object") {
    return {
      kind: "unsupported",
      status: null,
      simulated: false,
      mode: "live",
      filledQuantity: null,
      hasExecution: false,
    };
  }

  const record = response as Record<string, unknown>;
  const rawMode = record["mode"];
  const hasCamelSimulation = "isSimulated" in record;
  const hasSnakeSimulation = "is_simulated" in record;
  const rawSimulation = hasCamelSimulation
    ? record["isSimulated"]
    : hasSnakeSimulation
      ? record["is_simulated"]
      : undefined;
  let metadataIsCoherent = true;
  if (
    (rawMode !== undefined && rawMode !== "live" && rawMode !== "paper") ||
    (rawSimulation !== undefined && typeof rawSimulation !== "boolean") ||
    (rawMode === "paper" && rawSimulation !== true) ||
    (rawSimulation === true && rawMode !== "paper") ||
    (rawMode === "live" && rawSimulation === true)
  ) {
    metadataIsCoherent = false;
  }
  const simulated = rawMode === "paper" && rawSimulation === true;
  const mode: "live" | "paper" = simulated ? "paper" : "live";
  // Mappers may project durable live `new` into the local UI's `pending` enum.
  // Preserve the validated wire status separately so variant checks never run
  // against that lossy display projection.
  const rawStatus = record["createWireStatus"] ?? record["status"];
  const rawQuantity =
    record["quantityDecimal"] ??
    record["quantity_decimal"] ??
    record["quantity"];
  const quantity = typeof rawQuantity === "string" ? rawQuantity.trim() : null;
  const rawFilledQuantity =
    record["filledQuantity"] ??
    record["filled_quantity"] ??
    record["executedQuantityDecimal"] ??
    record["executed_quantity_decimal"];
  const filledQuantity =
    typeof rawFilledQuantity === "string" ? rawFilledQuantity.trim() : null;
  const hasExecution =
    filledQuantity !== null &&
    /^\d+(?:\.\d+)?$/.test(filledQuantity) &&
    !/^0+(?:\.0+)?$/.test(filledQuantity);
  const normalizedStatus =
    typeof rawStatus === "string" ? rawStatus.trim().toLowerCase() : null;
  const liveStatuses = new Set([
    "new",
    // The UI mapper can expose an acknowledged live `new` order as `pending`.
    // It is still a valid working lifecycle, not an unsupported response.
    "pending",
    "partiallyfilled",
    "partially_filled",
    "filled",
    "cancelled",
    "rejected",
    "expired",
  ]);
  const paperStatuses = new Set([
    "pending",
    "open",
    "partiallyfilled",
    "partially_filled",
    "filled",
    "cancelled",
    "rejected",
    "expired",
  ]);
  if (
    normalizedStatus === null ||
    !(simulated ? paperStatuses : liveStatuses).has(normalizedStatus)
  ) {
    metadataIsCoherent = false;
  }
  const rawSubmissionState =
    record["submissionState"] ?? record["submission_state"];
  const rawCreateDisposition =
    record["createDisposition"] ?? record["create_disposition"];
  const submissionState =
    typeof rawSubmissionState === "string"
      ? rawSubmissionState.trim().toLowerCase()
      : null;
  const createDisposition =
    typeof rawCreateDisposition === "string"
      ? rawCreateDisposition.trim().toLowerCase()
      : null;
  const validSubmissionStates = new Set([
    "acknowledged",
    "dispatching",
    "reconciliation_required",
    "rejected",
    "not_applicable",
  ]);
  const validCreateDispositions = new Set([
    "created",
    "idempotent_replay",
    "legacy_idempotent_replay",
    "pending_reconciliation",
  ]);
  if (
    (rawSubmissionState !== undefined &&
      (submissionState === null ||
        !validSubmissionStates.has(submissionState))) ||
    (rawCreateDisposition !== undefined &&
      (createDisposition === null ||
        !validCreateDispositions.has(createDisposition))) ||
    (submissionState === "rejected" && normalizedStatus !== "rejected") ||
    (submissionState === "not_applicable" &&
      !simulated &&
      createDisposition !== "legacy_idempotent_replay")
  ) {
    metadataIsCoherent = false;
  }
  if (submissionState !== null && createDisposition !== null) {
    const allowedPair =
      (simulated &&
        submissionState === "not_applicable" &&
        (createDisposition === "created" ||
          createDisposition === "idempotent_replay")) ||
      (!simulated &&
        submissionState === "acknowledged" &&
        (createDisposition === "created" ||
          createDisposition === "idempotent_replay")) ||
      (!simulated &&
        (submissionState === "dispatching" ||
          submissionState === "reconciliation_required") &&
        createDisposition === "pending_reconciliation") ||
      (!simulated &&
        submissionState === "not_applicable" &&
        createDisposition === "legacy_idempotent_replay") ||
      (!simulated &&
        submissionState === "rejected" &&
        (createDisposition === "created" ||
          createDisposition === "idempotent_replay"));
    if (!allowedPair) metadataIsCoherent = false;
  }

  const reconciliationRequired = isOrderReconciliationRequired(response);

  let kind: CreateOrderOutcomeKind;
  if (reconciliationRequired) {
    kind = "reconciliation";
  } else {
    switch (normalizedStatus) {
      case "new":
      case "pending":
      case "open":
        kind = "working";
        break;
      case "partiallyfilled":
      case "partially_filled":
        kind = "partially_filled";
        break;
      case "filled":
        kind = "filled";
        break;
      case "cancelled":
      case "canceled":
      case "rejected":
      case "expired":
        kind = "terminal";
        break;
      default:
        kind = "unsupported";
        break;
    }
  }

  if (!metadataIsCoherent) kind = "unsupported";

  const filledComparison =
    quantity !== null && filledQuantity !== null
      ? compareUnsignedDecimalStrings(filledQuantity, quantity)
      : null;
  if (
    quantity !== null &&
    filledQuantity !== null &&
    filledComparison === null
  ) {
    kind = "unsupported";
  } else if (filledComparison !== null && filledComparison > 0) {
    // Never accept a response claiming execution beyond requested quantity,
    // including reconciliation-required responses.
    kind = "unsupported";
  } else if (filledComparison !== null) {
    const lifecycleIsCoherent =
      ((normalizedStatus === "new" ||
        normalizedStatus === "pending" ||
        normalizedStatus === "open") &&
        !hasExecution) ||
      ((normalizedStatus === "partiallyfilled" ||
        normalizedStatus === "partially_filled") &&
        hasExecution &&
        filledComparison < 0) ||
      (normalizedStatus === "filled" && filledComparison === 0) ||
      (normalizedStatus === "rejected" && !hasExecution) ||
      ((normalizedStatus === "cancelled" ||
        normalizedStatus === "canceled" ||
        normalizedStatus === "expired") &&
        filledComparison < 0);
    if (!lifecycleIsCoherent) {
      kind = "unsupported";
    }
  }

  return {
    kind,
    status: typeof rawStatus === "string" ? rawStatus : null,
    simulated,
    mode,
    filledQuantity,
    hasExecution,
  };
}

function compareUnsignedDecimalStrings(
  left: string,
  right: string,
): -1 | 0 | 1 | null {
  const normalize = (value: string): readonly [string, string] | null => {
    if (!/^\d+(?:\.\d+)?$/.test(value)) return null;
    const [rawInteger, rawFraction = ""] = value.split(".");
    return [
      rawInteger.replace(/^0+(?=\d)/, ""),
      rawFraction.replace(/0+$/, ""),
    ];
  };
  const a = normalize(left);
  const b = normalize(right);
  if (a === null || b === null) return null;
  if (a[0].length !== b[0].length) return a[0].length < b[0].length ? -1 : 1;
  if (a[0] !== b[0]) return a[0] < b[0] ? -1 : 1;
  const scale = Math.max(a[1].length, b[1].length);
  const aFraction = a[1].padEnd(scale, "0");
  const bFraction = b[1].padEnd(scale, "0");
  if (aFraction === bFraction) return 0;
  return aFraction < bFraction ? -1 : 1;
}
